/**
 * Arena **projectile entities** - the sim half of the bullet-hell rework (PRD
 * #399 Slice A). A projectile is a lightweight, server-authoritative world
 * entity (the `vortex`/`rat`/`horde` marker precedent) that *flies*: it carries
 * a per-tick velocity, a payload (damage / remaining pierce / owner), a lifetime,
 * and a cosmetic `look`, and it advances its own {@link PositionComponent} every
 * tick. It has **no `mobile`** - a bolt is not a grid-locked walker; it lerps
 * across fractional tiles by its own velocity, so the client renders it straight
 * off its replicated position with no gait smoothing.
 *
 * This slice is deliberately motion-only:
 *
 *  - {@link spawnProjectile} places a projectile with a velocity from a unit
 *    direction and {@link ARENA_PROJECTILE_TILES_PER_S}.
 *  - {@link stepProjectiles} advances every projectile one tick and expires it on
 *    its {@link ProjectileComponent.ttlTicks} (which also caps its range, since
 *    the ttl is derived from {@link ARENA_PROJECTILE_MAX_RANGE_TILES} at spawn).
 *
 * There is **no firing** (Slice B converts `fireSkill` to spawn these) and **no
 * collision/damage** (Slice C's `stepProjectiles(world, enemies)` collides them
 * through the shared {@link ./combat} seam). A hand-spawned bolt just travels and
 * replicates. The whole module is pure and clock-free (the `arena.test.ts`
 * discipline): the only time input is the number of {@link stepProjectiles} calls,
 * and no slice-A behaviour needs randomness, so it never touches `world.rng`.
 */
import type { Entity, EntityId, PositionComponent } from './entity';
import type { World } from './world';
import type { ProjectileEffectId } from './content';
import { directionForDelta, Direction } from './direction';
import { applyDamage, isDead } from './combat';

/**
 * The server's fixed step, in seconds - the 50ms `STEP_MS` the whole sim ticks at
 * (mirrored by `ARENA_TICK_MS`). A projectile's on-the-wire velocity is stored in
 * **tiles per tick** so {@link stepProjectiles} is a trivial clock-free add
 * (`position += velocity`), so this only converts the human-facing tiles-per-second
 * speed into that per-tick displacement at spawn time.
 */
const TICK_SECONDS = 0.05;

/**
 * Default projectile flight speed, in **tiles per second**. Seeded from the current
 * cosmetic `PROJECTILE_TILES_PER_S` (`src/render/volleyFx.ts`) so the travel feel
 * carries over unchanged when Slice B makes it real. The tuning lever the PRD calls
 * out first (speed vs. enemy gait); a later slice may thread a per-build override.
 */
export const ARENA_PROJECTILE_TILES_PER_S = 10;

/**
 * How far, in tiles, a projectile flies before it expires. This bounds a shot's
 * reach so a bolt that hits nothing dissipates near the edge of the runner's
 * screen rather than crossing the whole endless plain. The lifetime
 * ({@link ARENA_PROJECTILE_TTL_TICKS}) is derived from it, so range and ttl can
 * never disagree - there is no separate range field on the wire component.
 */
export const ARENA_PROJECTILE_MAX_RANGE_TILES = 14;

/** Per-tick displacement (tiles) at the default speed - the velocity magnitude of a shot. */
const DEFAULT_TILES_PER_TICK = ARENA_PROJECTILE_TILES_PER_S * TICK_SECONDS;

/**
 * A projectile's lifetime in ticks at the default speed: {@link
 * ARENA_PROJECTILE_MAX_RANGE_TILES} / per-tick distance, rounded up so the shot
 * reaches its full range before the final tick removes it. Expiring on this tick
 * count *is* the max-range cap (the component carries no range field).
 */
export const ARENA_PROJECTILE_TTL_TICKS = Math.ceil(
  ARENA_PROJECTILE_MAX_RANGE_TILES / DEFAULT_TILES_PER_TICK,
);

/** The look a hand-spawned bolt draws with when a caller does not pick one. */
const DEFAULT_LOOK: ProjectileEffectId = 'magic-arrow';

/**
 * The last replicated {@link ProjectileComponent.ttlTicks} at or below which a
 * *removed* projectile is read as having **expired at max range** (a soft,
 * silent dissipation) rather than having **struck something** (an impact flash +
 * thump). This is the pure decision behind the client's impact-on-removal cue
 * (PRD #399 Slice D): the projectile entity is on the wire, so the client draws
 * it from its live position and derives its impact from the snapshot diff - the
 * SoundObserver-from-snapshot pattern (the moongate precedent, ADR-0193) - never
 * a resolved FX cascade.
 *
 * The signal is exact at the sim level and merely *fuzzed* by the wire, which is
 * why ttl (not position) is the discriminator. {@link stepProjectiles} removes a
 * projectile for **exactly two** reasons - a contact whose pierce budget is
 * spent, or `ttlTicks` reaching 0 (also its max-range cap) - so a bolt removed
 * with ttl still high was cut short by a contact, while one removed with ttl
 * already near 0 lived its whole flight and faded. The floor absorbs the
 * broadcast cadence (ADR-0163): the last snapshot before an expiry sees ttl in
 * `[1, broadcastInterval]`, so any broadcast-interval's worth of remaining life
 * reads as "about to expire". A contact within that last window (a strike at the
 * very edge of range) reads as an expiry too - a minor, documented ambiguity;
 * the enemy's own damage float still communicates that hit.
 */
export const ARENA_PROJECTILE_EXPIRY_TTL = 2;

/**
 * Whether a projectile removed with `lastTtlTicks` remaining struck an enemy (its
 * impact cue should fire) versus expired at max range (it dissipates silently).
 * See {@link ARENA_PROJECTILE_EXPIRY_TTL}. Pure and clock-free, shared by the
 * client's audio (`SoundObserver`) and visual (`GameRenderer.syncProjectiles`)
 * impact seams so the flash and the thump can never disagree.
 */
export function projectileRemovalWasContact(lastTtlTicks: number): boolean {
  return lastTtlTicks > ARENA_PROJECTILE_EXPIRY_TTL;
}

/**
 * An enemy's collision half-extent, in tiles: it occupies the 1x1 square centred
 * on its tile ({@link ARENA_PROJECTILE_HIT_RADIUS} in each axis). A projectile is
 * a point; a hit is the point's swept segment crossing that square (see
 * {@link stepProjectiles}). 0.5 keeps collision a clean single-tile footprint -
 * a shot aimed at a mover that has slipped to the neighbouring tile misses, which
 * is the whole dodge mechanic (PRD #399).
 */
export const ARENA_PROJECTILE_HIT_RADIUS = 0.5;

/** Where a projectile is launched from (its origin tile; `z` defaults to ground). */
export interface ProjectileOrigin {
  x: number;
  y: number;
  z?: number;
}

/** A launch direction as a (dx, dy) vector; normalized to a unit heading by {@link spawnProjectile}. */
export interface ProjectileDir {
  x: number;
  y: number;
}

/** The payload and cosmetics a caller hands {@link spawnProjectile}. */
export interface SpawnProjectileOptions {
  /** The firer's entity id, carried so Slice C can attribute kills / skip the owner. */
  ownerId: EntityId;
  /** Damage this projectile deals on contact (applied by Slice C, not here). */
  damage: number;
  /**
   * How many *additional* enemies past the first the shot punches through
   * (Slice C's pierce). Absent = 0, a single-hit bolt. Carried now so the wire
   * shape is final from Slice A.
   */
  pierceLeft?: number;
  /** Flight speed override, tiles per second; absent uses {@link ARENA_PROJECTILE_TILES_PER_S}. */
  speedTilesPerS?: number;
  /** Lifetime override, ticks; absent derives it from {@link ARENA_PROJECTILE_MAX_RANGE_TILES}. */
  ttlTicks?: number;
  /** The projectile art strip the client draws; absent uses {@link DEFAULT_LOOK}. */
  look?: ProjectileEffectId;
}

/**
 * Spawn one projectile entity and register it. Its velocity is the **unit** of
 * `dir` scaled to the per-tick displacement of `speedTilesPerS` (so it advances the
 * same distance regardless of how long the caller's `dir` vector was), and its
 * `position.facing` is derived from that heading so the drawn bolt points the right
 * way. A zero-length `dir` degrades to due east rather than producing a NaN
 * velocity (a caller error, but never a corrupt entity).
 *
 * The returned entity carries a {@link PositionComponent} (fractional, since it
 * flies between tiles) and a {@link ProjectileComponent}, and deliberately **no
 * `mobile`** - {@link stepProjectiles} owns its motion, not the walk system. No
 * firing decision or collision happens here; the caller (Slice B) decides when and
 * whence to spawn.
 */
export function spawnProjectile(
  world: World,
  from: ProjectileOrigin,
  dir: ProjectileDir,
  opts: SpawnProjectileOptions,
): ReturnType<World['createEntity']> {
  const len = Math.hypot(dir.x, dir.y);
  const ux = len > 0 ? dir.x / len : 1;
  const uy = len > 0 ? dir.y / len : 0;
  const perTick = (opts.speedTilesPerS ?? ARENA_PROJECTILE_TILES_PER_S) * TICK_SECONDS;
  const vx = ux * perTick;
  const vy = uy * perTick;
  const facing = directionForDelta(ux, uy) ?? Direction.E;

  const position: PositionComponent = { x: from.x, y: from.y, facing };
  if (from.z) position.z = from.z;

  return world.createEntity({
    position,
    projectile: {
      vx,
      vy,
      damage: opts.damage,
      pierceLeft: opts.pierceLeft ?? 0,
      ownerId: opts.ownerId,
      ttlTicks: opts.ttlTicks ?? ARENA_PROJECTILE_TTL_TICKS,
      look: opts.look ?? DEFAULT_LOOK,
    },
  });
}

/**
 * One projectile-vs-enemy contact resolved by {@link stepProjectiles}: the damage
 * was **already applied** through the shared {@link ./combat} seam (the sim seam
 * broadcasts nothing - the ADR-0152 discipline), so this is a cue for the server to
 * float a damage number over the still-live enemy sprite on the contact tick. A
 * `dead` contact leaves the enemy at zero health; the arena reaper (`enemy-die`)
 * plays the death clip and despawns it next tick, unchanged.
 */
export interface ProjectileHit {
  /** The projectile that landed the hit (still flying if it pierced, else despawned this tick). */
  projectile: EntityId;
  /** The struck enemy - the sprite the damage float renders over. */
  target: EntityId;
  /** The damage this contact dealt (the projectile's payload). */
  damage: number;
  /** Whether this contact brought the enemy to zero health (a kill the reaper finishes). */
  dead: boolean;
}

/**
 * Per-projectile **hit memory**, so a piercing shot damages each enemy it passes
 * through **exactly once** even if that enemy re-enters the lane on a later tick
 * (the PRD's dedup contract). Keyed by the projectile entity itself in a WeakMap so
 * it lives entirely off the wire (the {@link ProjectileComponent} stays the compact
 * 7-field codec shape) and off persistence, and is collected automatically when the
 * projectile is removed - no manual cleanup, no cross-run leakage, and no effect on
 * determinism (membership is content-, not identity-, keyed at the point of use).
 */
const hitSets = new WeakMap<Entity, Set<EntityId>>();

/**
 * Where the swept segment `p0 -> p1` first enters the 1x1 square centred on
 * `(cx, cy)` with half-extent {@link ARENA_PROJECTILE_HIT_RADIUS}, as a parameter
 * `t in [0, 1]` along the segment, or `null` if it never touches the square. A
 * slab (segment-vs-AABB) test, so a **fast** shot whose endpoints straddle a thin
 * enemy still registers (no tunneling) - an endpoint-only Chebyshev check would
 * skip it. The returned `t` orders multiple pierced enemies nearest-first along
 * travel.
 */
function segmentEntersTile(
  p0x: number,
  p0y: number,
  p1x: number,
  p1y: number,
  cx: number,
  cy: number,
): number | null {
  const h = ARENA_PROJECTILE_HIT_RADIUS;
  const dx = p1x - p0x;
  const dy = p1y - p0y;
  let tmin = 0;
  let tmax = 1;
  // X slab.
  if (Math.abs(dx) < 1e-12) {
    if (p0x < cx - h || p0x > cx + h) return null; // parallel and outside
  } else {
    let t1 = (cx - h - p0x) / dx;
    let t2 = (cx + h - p0x) / dx;
    if (t1 > t2) [t1, t2] = [t2, t1];
    tmin = Math.max(tmin, t1);
    tmax = Math.min(tmax, t2);
    if (tmin > tmax) return null;
  }
  // Y slab.
  if (Math.abs(dy) < 1e-12) {
    if (p0y < cy - h || p0y > cy + h) return null;
  } else {
    let t1 = (cy - h - p0y) / dy;
    let t2 = (cy + h - p0y) / dy;
    if (t1 > t2) [t1, t2] = [t2, t1];
    tmin = Math.max(tmin, t1);
    tmax = Math.min(tmax, t2);
    if (tmin > tmax) return null;
  }
  return tmin;
}

/**
 * Advance every projectile by one tick and collide it against the live enemy set.
 *
 * For each projectile: move it along its velocity (`position += velocity`, the
 * velocity being a per-tick displacement), then test the **segment it just swept**
 * against the **current** tile of every enemy in `enemies` ({@link
 * segmentEntersTile}, a segment-vs-tile test so a fast shot cannot tunnel a thin
 * enemy). Enemies the segment crosses are struck **nearest-first**; each contact
 * applies the projectile's `damage` through the shared {@link ./combat} seam
 * ({@link applyDamage}), records the enemy in the projectile's {@link hitSets hit
 * memory} (so it is never double-hit, even on a later tick), and **decrements
 * pierce**. A projectile despawns the moment its pierce budget is spent (a
 * `pierceLeft = 0` bolt after one hit, a `pierceLeft = n` bolt after `n + 1`), or
 * when its {@link ProjectileComponent.ttlTicks} reaches zero (also its max-range
 * cap). A dead-but-unreaped enemy and the projectile's own owner are skipped.
 *
 * Collision is brute-force **O(enemies x projectiles)** - a few thousand cheap
 * segment checks per run per tick at the #387 enemy cap. A uniform-grid spatial
 * hash of enemy tiles is the known optimisation if dense late-game volleys ever
 * profile hot; it is a **deliberate deferral** (out of this slice - see PRD #399 /
 * the Slice D ADR), not an omission.
 *
 * Returns one {@link ProjectileHit} per contact (in resolution order) for the
 * server to float a damage number over the live sprite; the damage is already
 * applied here (the sim seam broadcasts nothing). Pure and clock-free: the only
 * time input is being called once per server tick, so a seeded run replays
 * byte-identically. Called with no `enemies` (the Slice A motion-only signature),
 * it just flies + expires and returns no hits.
 */
export function stepProjectiles(world: World, enemies?: Iterable<EntityId>): ProjectileHit[] {
  // Nothing in flight: bail before touching the enemy set (PRD #825). The
  // subsystem is dormant in the Arena since ADR-0216, so this is the answer on
  // every tick of every run - and the old shape paid a full `world.all()` scan
  // plus a freshly built ~150-element enemy array for it. The projectile
  // presence index makes the empty case O(1).
  const projectiles = world.withComponent('projectile');
  if (projectiles.size === 0) return [];

  const enemyEntities: Entity[] = [];
  if (enemies) {
    for (const id of enemies) {
      const enemy = world.get(id);
      // Skip a missing/dead-but-unreaped enemy: a corpse takes no more hits.
      if (enemy && enemy.position && !isDead(enemy)) enemyEntities.push(enemy);
    }
  }

  const hits: ProjectileHit[] = [];
  const removed: EntityId[] = [];
  // Iterate the index, not the world: only entities that actually carry a
  // projectile. Removal is deferred to after the loop, so the index this walks
  // is never mutated mid-iteration.
  for (const entity of projectiles) {
    const projectile = entity.projectile;
    const position = entity.position;
    if (!projectile || !position) continue;

    const p0x = position.x;
    const p0y = position.y;
    position.x += projectile.vx;
    position.y += projectile.vy;
    projectile.ttlTicks -= 1;

    // Collide the swept segment against live enemies (nearest-first).
    if (enemyEntities.length > 0) {
      let memory = hitSets.get(entity);
      const contacts: { enemy: Entity; t: number }[] = [];
      for (const enemy of enemyEntities) {
        if (enemy.id === projectile.ownerId) continue;
        if (memory?.has(enemy.id)) continue;
        const t = segmentEntersTile(p0x, p0y, position.x, position.y, enemy.position!.x, enemy.position!.y);
        if (t !== null) contacts.push({ enemy, t });
      }
      contacts.sort((a, b) => a.t - b.t);
      let despawned = false;
      for (const { enemy } of contacts) {
        if (!memory) {
          memory = new Set();
          hitSets.set(entity, memory);
        }
        memory.add(enemy.id);
        const { dead } = applyDamage(enemy, projectile.damage);
        hits.push({ projectile: entity.id, target: enemy.id, damage: projectile.damage, dead });
        if (projectile.pierceLeft <= 0) {
          removed.push(entity.id);
          despawned = true;
          break;
        }
        projectile.pierceLeft -= 1;
      }
      if (despawned) continue; // already scheduled for removal; skip the ttl check
    }

    if (projectile.ttlTicks <= 0) removed.push(entity.id);
  }
  for (const id of removed) world.remove(id);
  return hits;
}
